import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { LatestSale } from './ItemAnalytics';
import { formatMoney, moneyFromMinor } from '../../../utils/UnitConversion.utils';

interface Props {
  sales: LatestSale[];
}

const ItemLatestSales: React.FC<Props> = ({ sales }) => {
  const hasSales = Array.isArray(sales) && sales.length > 0;

  if (!hasSales) {
    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-600">
          <HistoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
          <span className="text-sm font-medium italic uppercase tracking-widest opacity-50">No recent sales found</span>
        </div>
    );
  }

  return (
      <div className="h-full overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700">
        <TableContainer component={Paper} className="bg-transparent shadow-none border-none overflow-visible">
          <Table size="small" stickyHeader className="border-separate border-spacing-y-1">
            <TableHead>
              <TableRow>
                <TableCell
                    className="!bg-neutral-100 dark:!bg-[#1a1d23] !text-[10px] !font-black !text-gray-400 !uppercase !tracking-widest !border-none !py-2"
                >
                  Price
                </TableCell>
                <TableCell
                    className="!bg-neutral-100 dark:!bg-[#1a1d23] !text-[10px] !font-black !text-gray-400 !uppercase !tracking-widest !border-none !py-2"
                >
                  Wear / Float
                </TableCell>
                <TableCell
                    className="!bg-neutral-100 dark:!bg-[#1a1d23] !text-[10px] !font-black !text-gray-400 !uppercase !tracking-widest !border-none !py-2"
                >
                  Seed
                </TableCell>
                <TableCell
                    align="right"
                    className="!bg-neutral-100 dark:!bg-[#1a1d23] !text-[10px] !font-black !text-gray-400 !uppercase !tracking-widest !border-none !py-2"
                >
                  Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.map((sale, i) => {
                const priceValue = parseInt(sale.price || "0");
                const floatValue = sale.float && parseFloat(sale.float) !== 0 ? parseFloat(sale.float).toFixed(9) : null;
                const seedValue = sale.paintSeed && sale.paintSeed !== "0" ? `#${sale.paintSeed}` : null;

                return (
                    <TableRow
                        key={i}
                        className="group bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03] transition-all duration-200"
                    >
                      <TableCell className="!border-none !py-3">
                        <span className="font-bold text-dark-primary text-sm">
                          {formatMoney(moneyFromMinor(priceValue))} €
                        </span>
                      </TableCell>

                      <TableCell className="!border-none !py-3">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold text-gray-300 uppercase tracking-tighter">
                            {sale.wearName ? sale.wearName.replace(/_/g, ' ') : ''}
                          </span>
                          {floatValue && (
                              <span className="text-[11px] text-gray-400 font-mono italic">
                                {floatValue}
                              </span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="!border-none !py-3">
                        <span className="text-[11px] font-mono font-bold text-gray-400">
                          {seedValue || ''}
                        </span>
                      </TableCell>

                      <TableCell align="right" className="!border-none !py-3">
                        <span className="text-[11px] text-gray-500 font-medium">
                          {sale.soldDate ? sale.soldDate.split(' ')[0] : ''}
                        </span>
                      </TableCell>
                    </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
  );
};

export default ItemLatestSales;